import { useState, useEffect } from "react";
import { Check, X, RotateCcw, Volume2, ArrowLeft, ChevronRight, Trophy, Zap } from "lucide-react";
import lessons from "../data/lessons.json";

function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Build questions from current lesson — uses the sentence words directly
function buildQuestions(currentLesson) {
  const questions = [];

  // Main sentence from this lesson
  questions.push({
    correct: currentLesson.sentence.split(" "),
    breakdown: currentLesson.words,
    topic: currentLesson.topic,
  });

  // Auto-generate a second question from a nearby lesson in the same level
  const sameLevelLessons = lessons.filter(
    (l) => l.level === currentLesson.level && l.day !== currentLesson.day
  );
  if (sameLevelLessons.length > 0) {
    const bonus = sameLevelLessons[Math.floor(Math.random() * sameLevelLessons.length)];
    questions.push({
      correct: bonus.sentence.split(" "),
      breakdown: bonus.words,
      topic: bonus.topic,
    });
  }

  return questions;
}

export default function SentenceBuilder({ lesson }) {
  const [questions] = useState(() => buildQuestions(lesson));
  const [currentQ, setCurrentQ] = useState(0);
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const q = questions[currentQ];

  // Reset words when question changes
  useEffect(() => {
    setAvailable(shuffleArray(q.correct));
    setSelected([]);
    setResult(null);
  }, [currentQ, q.correct]);

  function handleSelect(word, index) {
    if (result) return;
    setSelected((prev) => [...prev, word]);
    setAvailable((prev) => prev.filter((_, i) => i !== index));
  }

  function handleRemove(word, index) {
    if (result) return;
    setAvailable((prev) => [...prev, word]);
    setSelected((prev) => prev.filter((_, i) => i !== index));
  }

  function handleCheck() {
    const isCorrect = selected.join(" ") === q.correct.join(" ");
    setResult(isCorrect ? "correct" : "incorrect");
    if (isCorrect) setScore((s) => s + 1);
  }

  function handleReset() {
    setAvailable(shuffleArray(q.correct));
    setSelected([]);
    setResult(null);
  }

  function handleNext() {
    if (currentQ < questions.length - 1) {
      setCurrentQ((c) => c + 1);
    } else {
      setCompleted(true);
    }
  }

  function handleRestart() {
    setCurrentQ(0);
    setScore(0);
    setCompleted(false);
  }

  function speak(text) {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.85;
      u.lang = "en-US";
      window.speechSynthesis.speak(u);
    }
  }

  // Completion screen
  if (completed) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trophy className="w-8 h-8 text-gold-700" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Sentence Builder Complete!
        </h2>
        <p className="text-4xl font-bold text-gold-700 mb-2">
          {score}/{questions.length}
        </p>
        <p className="text-gray-500 mb-6">
          {pct === 100
            ? "Perfect score! You nailed every sentence!"
            : pct >= 60
            ? "Good job! Practice makes perfect."
            : "Keep practicing! You'll get better."}
        </p>
        <button
          onClick={handleRestart}
          className="inline-flex items-center gap-2 bg-ink-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-ink-900 hover:scale-105 transition-all"
        >
          <RotateCcw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Zap className="w-5 h-5 text-gold-700" />
          Sentence Builder
        </h2>
        <span className="text-sm text-gray-400">
          {currentQ + 1} / {questions.length}
        </span>
      </div>

      {/* Progress dots */}
      <div className="flex gap-1.5 mb-6">
        {questions.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all ${
              i < currentQ
                ? "bg-sage-light"
                : i === currentQ
                ? "bg-ink-900"
                : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      <p className="text-gray-500 mb-4">
        Tap the words in the correct order to build the sentence.
      </p>

      {/* Listen button */}
      <button
        onClick={() => speak(q.correct.join(" "))}
        className="inline-flex items-center gap-2 bg-paper-deep text-gold-700 px-4 py-2 rounded-lg font-medium hover:bg-cream-100 transition-all mb-4 border border-ink-100"
      >
        <Volume2 className="w-4 h-4" />
        Listen First
      </button>

      {/* Selected words (sentence area) */}
      <div className="min-h-[72px] bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-4 mb-4 flex flex-wrap gap-2 items-center">
        {selected.length === 0 && (
          <p className="text-gray-400 text-sm">
            Tap words below to build your sentence...
          </p>
        )}
        {selected.map((word, i) => (
          <button
            key={`s-${i}`}
            onClick={() => handleRemove(word, i)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium transition-all ${
              result === "correct"
                ? "bg-sage text-white"
                : result === "incorrect"
                ? "bg-berry text-white"
                : "bg-ink-900 text-white hover:bg-ink-900 hover:scale-105"
            }`}
          >
            {word}
            {!result && <ArrowLeft className="w-3.5 h-3.5 opacity-60" />}
          </button>
        ))}
      </div>

      {/* Available words */}
      <div className="flex flex-wrap gap-2 mb-6">
        {available.map((word, i) => (
          <button
            key={`a-${i}`}
            onClick={() => handleSelect(word, i)}
            className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-cream-100 hover:text-gold-700 hover:scale-105 transition-all border border-gray-200"
          >
            {word}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        {!result ? (
          <>
            <button
              onClick={handleCheck}
              disabled={selected.length === 0}
              className="inline-flex items-center gap-2 bg-sage text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-sage hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              Check
            </button>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-gray-200 hover:scale-105 transition-all"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-gray-200 hover:scale-105 transition-all"
            >
              <RotateCcw className="w-4 h-4" />
              Retry
            </button>
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-2 bg-ink-900 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-ink-900 hover:scale-105 transition-all"
            >
              {currentQ < questions.length - 1 ? "Next" : "Finish"}
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Result: Correct */}
      {result === "correct" && (
        <div className="mt-6 space-y-4">
          <div className="bg-sage-light border-2 border-sage-light rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-sage-light flex items-center justify-center">
                <Check className="w-5 h-5 text-sage-dark" />
              </div>
              <p className="text-sage-dark font-bold text-lg">
                Perfect! You got it right!
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <p className="text-sage-dark font-medium text-lg">
                {q.correct.join(" ")}
              </p>
              <button
                onClick={() => speak(q.correct.join(" "))}
                className="p-1.5 rounded-full bg-sage-light text-sage-dark hover:bg-sage-light transition-all"
              >
                <Volume2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Breakdown */}
          <div className="bg-paper-deep border border-ink-100 rounded-xl p-5">
            <p className="font-semibold text-gold-700 mb-3">
              Sentence Breakdown:
            </p>
            <div className="space-y-2">
              {q.breakdown.map((w, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="font-bold text-gold-700 min-w-[80px]">
                    {w.word}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-gray-700">
                    <strong>{w.role}</strong> — {w.meaning}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Result: Incorrect */}
      {result === "incorrect" && (
        <div className="mt-6 bg-berry-light border-2 border-berry-light rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-berry-light flex items-center justify-center">
              <X className="w-5 h-5 text-berry" />
            </div>
            <p className="text-berry font-bold text-lg">
              Not quite right. Keep trying!
            </p>
          </div>
          <p className="text-gray-600 mt-2">
            Correct sentence:{" "}
            <strong className="text-gray-800">{q.correct.join(" ")}</strong>
          </p>
        </div>
      )}
    </div>
  );
}
